/**
 * WHICH SERVICE PLAN a saved Support at Home strategy was priced against.
 *
 * WHY THIS EXISTS. A Support at Home strategy's headline dollar — "your share of care costs falls
 * to $3,929 a year" — is `budget x contribution rate`. Optimising moves the RATE; the Service
 * Builder sets the BUDGET. So a saved strategy's rates survive a change of services, and its
 * DOLLARS do not.
 *
 * Nothing recorded which plan a strategy had been priced against, so build → optimise → save → go
 * back and adjust the services left that saved figure quietly stale, with nothing on screen saying
 * so. Owner-flagged 2026-08-27 while deciding whether to put the Service Builder before Optimise —
 * and putting it first makes that path MORE common, not less, which is why this lands first.
 *
 * The basis is deliberately a summary, not the plan itself: what matters is whether the money has
 * moved, and a saved record should not carry a copy of a document that lives elsewhere. Two plans
 * with the same service count and the same annual value price identically, which is the only
 * question this has to answer.
 */
import { SUPPORT_AT_HOME_SERVICES } from './supportAtHomeServices.tsx';
import { computeServiceCostsForPerson } from './serviceBuilderCosts.ts';

/** One person's side of a service plan — service id → the quantity and frequency chosen. */
type ServiceSide = Record<string, { quantity?: number | string; frequency?: string } | undefined> | undefined;

export interface ServicePlanShape {
    client?: ServiceSide;
    partner?: ServiceSide;
}

export interface ServicePlanBasis {
    /** How many services were switched on, across both people. */
    serviceCount: number;
    /** The plan's annual value — the budget the contribution rates apply to. Rounded to the dollar. */
    annualValue: number;
    /**
     * True when the plan was the app's illustrative typical package rather than one the client
     * built. A strategy priced on an illustration is provisional in a way one priced on a real
     * plan is not, and saying which is the point.
     */
    isEstimated: boolean;
}

/**
 * The plan's annual value SPLIT BY CONTRIBUTION CATEGORY — what each rate is actually charged on.
 *
 * WHY THIS EXISTS (owner-caught, 2026-08-27). Optimise showed "your share of care costs $7,722 a
 * year" for a plan the Service Builder priced at $0. Two screens, one plan, and neither figure was
 * a typo.
 *
 * supportAtHomeContributionEngine splits the budget by the plan's `serviceDistribution` — and falls
 * back to an ASSUMED 40/35/25 of the whole budget when there isn't one. The plan handed to it from
 * Optimise is `{ client, partner }`: no `serviceDistribution`, and no `services` key either, so the
 * engine's `hasActualServices` check was false even for a real, built plan. Every contribution
 * quoted on that screen was therefore the assumed split of the household budget, whatever services
 * the client had actually chosen — which is why an all-clinical plan (0% contribution) still
 * reported thousands of dollars.
 *
 * Deriving the split here is what makes the two screens agree: the categories come from the same
 * catalogue and the same cost helper the Service Builder totals with.
 */
export function servicePlanDistribution(plan: ServicePlanShape | null | undefined): {
    clinical: number;
    independence: number;
    everyday: number;
} {
    const sides = [plan?.client, plan?.partner];
    return sides.reduce(
        (acc, side) => {
            const costs = computeServiceCostsForPerson(side || {}, {}, SUPPORT_AT_HOME_SERVICES);
            return {
                clinical: acc.clinical + (costs.totalClinical || 0),
                independence: acc.independence + (costs.totalIndependence || 0),
                everyday: acc.everyday + (costs.totalEveryday || 0),
            };
        },
        { clinical: 0, independence: 0, everyday: 0 },
    );
}

const countSide = (side: ServiceSide): number =>
    Object.values(side || {}).filter((d) => d && Number(d.quantity) > 0).length;

const valueSide = (side: ServiceSide): number =>
    computeServiceCostsForPerson(side || {}, {}, SUPPORT_AT_HOME_SERVICES).total;

/** Summarise a plan into the two numbers that decide whether it prices differently. */
export function describeServicePlan(plan: ServicePlanShape | null | undefined, isEstimated: boolean): ServicePlanBasis {
    return {
        serviceCount: countSide(plan?.client) + countSide(plan?.partner),
        annualValue: Math.round(valueSide(plan?.client) + valueSide(plan?.partner)),
        isEstimated,
    };
}

/**
 * Has the plan moved enough to restate a saved strategy's dollars?
 *
 * Returns false when either side is unknown: a saved strategy from before this was recorded has no
 * basis to compare, and warning about a difference we cannot demonstrate would be worse than
 * staying quiet. Silence here means "no evidence", never "verified unchanged".
 */
export function servicePlanBasisChanged(
    saved: ServicePlanBasis | null | undefined,
    current: ServicePlanBasis | null | undefined,
): boolean {
    if (!saved || !current) return false;
    // A dollar of rounding is not a change of plan; a service added or removed always is.
    return saved.serviceCount !== current.serviceCount || Math.abs(saved.annualValue - current.annualValue) > 1;
}

/** One line for the reader, or null when there is nothing to say. */
export function servicePlanBasisWarning(
    saved: ServicePlanBasis | null | undefined,
    current: ServicePlanBasis | null | undefined,
): string | null {
    if (!servicePlanBasisChanged(saved, current)) return null;
    const from = saved as ServicePlanBasis;
    const to = current as ServicePlanBasis;
    const movedTo = to.isEstimated ? 'the illustrative package' : 'the current service plan';
    return (
        `Priced against a service plan worth ${formatDollars(from.annualValue)} a year (${from.serviceCount} ` +
        `${from.serviceCount === 1 ? 'service' : 'services'}). ${capitalise(movedTo)} is now ` +
        `${formatDollars(to.annualValue)} a year (${to.serviceCount} ${to.serviceCount === 1 ? 'service' : 'services'}), ` +
        `so the contribution figures below are out of date. The rates still hold — it is the dollars they apply to that moved.`
    );
}

const formatDollars = (n: number): string => `$${Math.round(n).toLocaleString('en-AU')}`;
const capitalise = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);
